import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface RoadmapItemResponse {
  id: string;
  title: string;
  description: string;
  status: "done" | "in-progress" | "planned";
  category: string;
  sortOrder: number;
  voteCount: number;
  hasVoted: boolean;
  createdAt: string;
}

interface CreateRoadmapItemPayload {
  title: string;
  description: string;
  status?: "done" | "in-progress" | "planned";
  category: string;
  sortOrder?: number;
}

interface UpdateRoadmapItemPayload {
  title?: string;
  description?: string;
  status?: "done" | "in-progress" | "planned";
  category?: string;
  sortOrder?: number;
}

export function useRoadmapItems() {
  return useQuery<RoadmapItemResponse[]>({
    queryKey: ["roadmap"],
    queryFn: () => apiFetch<RoadmapItemResponse[]>("/roadmap"),
  });
}

export function useCreateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoadmapItemPayload) =>
      apiFetch<RoadmapItemResponse>("/roadmap", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
    },
  });
}

export function useUpdateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateRoadmapItemPayload & { id: string }) =>
      apiFetch<RoadmapItemResponse>(`/roadmap/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
    },
  });
}

export function useDeleteRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/roadmap/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
    },
  });
}

export function useToggleRoadmapVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiFetch<{ voted: boolean }>(`/roadmap/${itemId}/vote`, { method: "POST" }),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ["roadmap"] });
      const previous = queryClient.getQueryData<RoadmapItemResponse[]>(["roadmap"]);
      if (previous) {
        queryClient.setQueryData<RoadmapItemResponse[]>(["roadmap"], previous.map((item) =>
          item.id === itemId
            ? { ...item, hasVoted: !item.hasVoted, voteCount: item.voteCount + (item.hasVoted ? -1 : 1) }
            : item,
        ));
      }
      return { previous };
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["roadmap"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
    },
  });
}

export function useReorderRoadmapItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemIds: string[]) =>
      apiFetch<{ success: boolean }>("/roadmap/reorder", {
        method: "POST",
        body: JSON.stringify({ itemIds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
    },
  });
}
